const DEFAULT_LEADERBOARD_URL = 'https://9ezaul1dtk.execute-api.af-south-1.amazonaws.com/leaderboard';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function parseJsonResponse(xhr) {
  if (!xhr.responseText) {
    return null;
  }

  try {
    return JSON.parse(xhr.responseText);
  } catch {
    throw new Error('Leaderboard API returned invalid JSON.');
  }
}

function createApiError(xhr) {
  let message = `Leaderboard API request failed with status ${xhr.status}.`;

  try {
    const body = JSON.parse(xhr.responseText);

    if (body?.error || body?.message) {
      message = body.error || body.message;
    }
  } catch {
    if (xhr.responseText) {
      message = xhr.responseText;
    }
  }

  const error = new Error(message);
  error.status = xhr.status;

  return error;
}

function xhrJsonRequest(method, url, body) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open(method, url, true);
    xhr.setRequestHeader('Accept', 'application/json');

    if (body) {
      xhr.setRequestHeader('Content-Type', 'application/json');
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(parseJsonResponse(xhr));
        } catch (error) {
          reject(error);
        }

        return;
      }

      reject(createApiError(xhr));
    };

    xhr.onerror = () => reject(new Error('Leaderboard API network request failed.'));
    xhr.ontimeout = () => reject(new Error('Leaderboard API request timed out.'));
    xhr.timeout = 15000;

    xhr.send(body ? JSON.stringify(body) : null);
  });
}

function buildUrlWithQuery(url, params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });

  const queryString = query.toString();

  if (!queryString) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
}

export function isValidUuid(uuid) {
  return UUID_PATTERN.test(String(uuid || ''));
}

export function getLeaderboard({
  deviceId,
  url = DEFAULT_LEADERBOARD_URL,
} = {}) {
  if (!isValidUuid(deviceId)) {
    throw new Error('A valid UUID is required to load the leaderboard.');
  }

  return xhrJsonRequest(
    'GET',
    buildUrlWithQuery(url, { deviceId }),
  );
}

function leaderboardIncludesDevice(leaderboard, deviceId) {
  if (leaderboard?.deviceRank || leaderboard?.device) {
    return true;
  }

  const entries = Array.isArray(leaderboard?.entries) ? leaderboard.entries : [];

  return entries.some((entry) => (entry.deviceId || entry.id) === deviceId);
}

function isMissingDeviceError(error) {
  return error?.status === 404 || /not\s*found|missing|unknown/i.test(error?.message || '');
}

function createZeroScoreLeaderboard({ deviceId, playerName }) {
  const latestPlayedAt = new Date().toISOString();

  return {
    deviceId,
    deviceRank: 1,
    totalEntries: 1,
    fallback: true,
    entries: [
      {
        rank: 1,
        deviceId,
        playerName,
        score: 0,
        winStream: 0,
        correct: 0,
        total: 0,
        accuracy: 0,
        latestPlayedAt,
      },
    ],
  };
}

function isRecoverableLeaderboardLoadError(error) {
  return isMissingDeviceError(error) || /network request failed|timed out/i.test(error?.message || '');
}

function getDeviceEntry(leaderboard, deviceId) {
  const entries = Array.isArray(leaderboard?.entries) ? leaderboard.entries : [];

  return entries.find((entry) => (entry.deviceId || entry.id) === deviceId) || null;
}

export function postLeaderboardScore({
  deviceId,
  playerName,
  score,
  correct,
  total,
  winStream,
  url = DEFAULT_LEADERBOARD_URL,
}) {
  if (!isValidUuid(deviceId)) {
    throw new Error('A valid UUID is required to post a leaderboard score.');
  }

  const body = {
    deviceId,
    playerName,
    score,
    winStream,
    correct,
    total,
  };

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('POST', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(parseJsonResponse(xhr));
        } catch (error) {
          reject(error);
        }

        return;
      }

      reject(createApiError(xhr));
    };

    xhr.onerror = () => reject(new Error('Leaderboard API network request failed.'));
    xhr.ontimeout = () => reject(new Error('Leaderboard API request timed out.'));
    xhr.timeout = 15000;
    xhr.send(JSON.stringify(body));
  });
}

export async function getOrCreateLeaderboard({
  deviceId,
  playerName,
  url = DEFAULT_LEADERBOARD_URL,
} = {}) {
  let shouldCreateScore = false;

  try {
    const leaderboard = await getLeaderboard({ deviceId, url });

    if (leaderboardIncludesDevice(leaderboard, deviceId)) {
      return leaderboard;
    }

    shouldCreateScore = true;
  } catch (error) {
    if (!isRecoverableLeaderboardLoadError(error)) {
      throw error;
    }

    shouldCreateScore = true;
  }

  if (shouldCreateScore) {
    try {
      await postLeaderboardScore({
        deviceId,
        playerName,
        score: 0,
        correct: 0,
        total: 0,
        winStream: 0,
        url,
      });
    } catch {
      return createZeroScoreLeaderboard({ deviceId, playerName });
    }
  }

  try {
    return await getLeaderboard({ deviceId, url });
  } catch {
    return createZeroScoreLeaderboard({ deviceId, playerName });
  }
}

export async function updateLeaderboardPlayerName({
  deviceId,
  playerName,
  url = DEFAULT_LEADERBOARD_URL,
} = {}) {
  const leaderboard = await getOrCreateLeaderboard({ deviceId, playerName, url });
  const deviceEntry = getDeviceEntry(leaderboard, deviceId) || {};
  const correct = Number(deviceEntry.correct ?? deviceEntry.score) || 0;
  const total = Number(deviceEntry.total) || 0;
  const winStream = Number(deviceEntry.winStream ?? deviceEntry.bestStreak ?? deviceEntry.streak) || 0;

  return postLeaderboardScore({
    deviceId,
    playerName,
    score: Number(deviceEntry.score ?? correct) || 0,
    correct,
    total,
    winStream,
    url,
  });
}

export { DEFAULT_LEADERBOARD_URL };
